import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Sheet as SheetIcon } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, timeAgo } from "@/lib/constants";
import { Card, LoadingBlock, PageHeader, Spinner } from "@/components/common";
import { ColumnMapper } from "@/components/ColumnMapper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SettingsForm = ({ cfg, onSaved }) => {
  const { meta } = useApp();
  const [f, setF] = useState({ sheet_id: cfg.sheet_id || "", tab: cfg.tab || "", default_brand: cfg.default_brand || "", default_platform: cfg.default_platform || "Call", auto_enabled: cfg.auto_enabled !== false, mapping: cfg.mapping || {} });
  const [cols, setCols] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.put("/sync/config", { ...f, ...extra });
      toast.success("Sync settings saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const loadCols = async () => {
    try {
      const { data } = await api.get("/sync/columns");
      setCols(data);
      setF((x) => ({ ...x, mapping: data.suggested }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <Card title="Callers' sheet" subtitle="We only read this sheet. We never change it." testid="sync-settings-card">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2"><Label>Google Sheet link or ID</Label><Input data-testid="sync-sheet-id-input" className="h-11 rounded-xl" placeholder="https://docs.google.com/spreadsheets/d/... (leave blank to use the demo sheet)" value={f.sheet_id} onChange={(e) => setF({ ...f, sheet_id: e.target.value })} /></div>
          <div className="space-y-2"><Label>Tab name (optional)</Label><Input data-testid="sync-tab-input" className="h-11 rounded-xl" placeholder="First tab is used if empty" value={f.tab} onChange={(e) => setF({ ...f, tab: e.target.value })} /></div>
          <div className="space-y-2"><Label>Brand if the sheet has no brand column</Label>
            <Select value={f.default_brand || "__detect"} onValueChange={(v) => setF({ ...f, default_brand: v === "__detect" ? "" : v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="sync-default-brand"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__detect">Detect from outlet</SelectItem>{meta?.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Platform if the sheet doesn't say</Label>
            <Select value={f.default_platform} onValueChange={(v) => setF({ ...f, default_platform: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="sync-default-platform"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Call">Call</SelectItem><SelectItem value="Message">Message</SelectItem></SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-3 self-end pb-2 text-sm font-medium"><Switch checked={f.auto_enabled} onCheckedChange={(v) => setF({ ...f, auto_enabled: v })} data-testid="sync-auto-switch" />Sync automatically every 15 minutes</label>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button className="h-11 rounded-xl" onClick={() => save()} disabled={busy} data-testid="sync-save-settings">{busy ? <Spinner /> : "Save settings"}</Button>
          <Button variant="outline" className="h-11 rounded-xl" onClick={loadCols} data-testid="sync-load-columns">Match columns</Button>
        </div>
      </Card>
      {cols && (
        <Card title="Match the callers' columns" subtitle={`${cols.total_rows} rows found${cols.mode === "demo" ? " in the demo sheet" : ""}`} testid="sync-mapping-card">
          <ColumnMapper fields={meta.fields.filter((x) => x.mappable)} columns={cols.columns} mapping={f.mapping} onChange={(m) => setF({ ...f, mapping: m })} sample={cols.sample} testid="sync-column-mapper" />
          <Button className="mt-5 h-11 rounded-xl" onClick={() => save()} disabled={busy} data-testid="sync-save-mapping">Save matching</Button>
        </Card>
      )}
    </>
  );
};

export default function Sync() {
  const { can } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/sync/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/sync/runs").then((r) => setRuns(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sync/run");
      data.status === "ok" ? toast.success(`Synced: ${data.message}`) : toast.error(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!cfg) return <LoadingBlock rows={5} />;
  const ok = cfg.last_status === "ok";
  return (
    <div data-testid="sync-page" className="max-w-5xl space-y-6">
      <PageHeader title="Direct complaints sync" subtitle="Complaints your callers log in their Google Sheet are brought in here automatically.">
        {can("admin", "manager") && <Button className="h-12 rounded-xl px-6 text-base" onClick={syncNow} disabled={busy} data-testid="sync-now-button">{busy ? <Spinner /> : <RefreshCw className="mr-2 h-4 w-4" />}Sync now</Button>}
      </PageHeader>
      {cfg.mode === "demo" && (
        <div data-testid="sync-demo-banner" className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <SheetIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <div><b>Demo mode:</b> using a built-in sample callers' sheet so you can try the flow. Connect Google in Settings and paste your callers' sheet link below to go live.</div>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3 stagger">
        <Card testid="sync-last-synced"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last synced</div><div className="mt-2 font-display text-2xl font-bold">{timeAgo(cfg.last_synced_at)}</div><div className="text-sm text-slate-400">{fmtDateTime(cfg.last_synced_at)}</div></Card>
        <Card testid="sync-status"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</div>
          <div className={`mt-2 flex items-center gap-2 font-display text-2xl font-bold ${!cfg.last_status ? "text-slate-400" : ok ? "text-emerald-600" : "text-red-600"}`}>
            {!cfg.last_status ? "Not run yet" : ok ? <><CheckCircle2 className="h-6 w-6" />Working</> : <><AlertTriangle className="h-6 w-6" />Problem</>}
          </div><div className="text-sm text-slate-500">{cfg.last_message}</div></Card>
        <Card testid="sync-auto"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Automatic sync</div><div className="mt-2 font-display text-2xl font-bold">{cfg.auto_enabled !== false ? "Every 15 min" : "Off"}</div><div className="text-sm text-slate-400">{cfg.mode === "live" ? "Live sheet" : "Demo sheet"}</div></Card>
      </div>
      {can("admin") && <SettingsForm cfg={cfg} onSaved={load} />}
      <Card title="Recent syncs" testid="sync-runs">
        {runs.length ? (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-2">When</th><th>Started by</th><th>Result</th></tr></thead>
            <tbody>{runs.map((r) => (
              <tr key={r.id} className="border-b border-slate-100"><td className="whitespace-nowrap py-2.5 pr-3">{fmtDateTime(r.at)}</td><td className="pr-3">{r.trigger === "auto" ? "Automatic" : r.by}</td>
                <td className={r.status === "ok" ? "text-slate-700" : "text-red-600"}>{r.message}{r.errors ? ` · ${r.errors} rows had problems` : ""}</td></tr>
            ))}</tbody>
          </table>
        ) : <p className="text-sm text-slate-400">No syncs yet. Press "Sync now" to run the first one.</p>}
      </Card>
    </div>
  );
}
